import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ChatbotRTKService } from '../app/services/chatbot-rtk.services';
import { RootState } from '../app/store';
import IBotVersions from '../interfaces/models/Knowledge/bot-version.interface';
import ICreateKnowledge from '../interfaces/models/Knowledge/create-knowledge.interface';
import ICreateKnowledgeQuestion from '../interfaces/models/Knowledge/create-knowledge-question.interface';
import IKnowledgeState from '../interfaces/models/Knowledge/knowledge-state.interface';
import IUpdateKnowledgeAssistant from '../interfaces/models/Knowledge/update-knowledge-assistant.interface';
import IXlSFile from '../interfaces/models/Knowledge/xlS-file.interface';
import IAssistant from '../interfaces/response/assistant.interface';
import { BotTypeEnum } from '../utils/BotTypeEnum';

const initialState: IKnowledgeState = {
  create: {
    botType: 'Widget',
    isSideMenu: true,
    isEdit: false,
    currentStep: 'main',
    stepNumber: 1,
    step: ['main', 'upload-data', 'select-columns'],
    scenarioSteps: ['main', 'mainStarterKit', 'conditional-scenario'],
    VideoScenarioSteps: ['main', 'video-scenario'],
    assistantBotSteps: ['main', 'assistant-speed', 'assistant-choose-type', 'assistant-bot'],
    kbAssistantBotSteps: ['main', 'assistant-speed', 'kb-assistant-choose-type', 'kb-assistant-bot', 'kb-assistant-upload-file'],
    ppAssistantBotSteps: ['main', 'assistant-speed', 'pp-assistant-insert-domain', 'pp-assistant-auto-update'],
    assistantCloneBotSteps: ['main', 'assistant-speed', 'assistant-clone'],
    isNextStep: true,
    isPreviousStep: false,
    fileXls: null,
    fileXlsValue: [],
    fileXlsName: '',
    chatbotTitle: '',
    chatbotPrevTitle: '',
    chatbotService: '',
    chatbotModel: '',
    inputs: [],
    outputs: [],
    questions: [],
    assistant: null,
    assistantWelcomeMessage: '',
    assistantCompanyName: '',
    assistantAgentName: '',
    additional_instruction: '',
    assistantInstructions: {},
    files: [],
    domainAddress: '',
    ppAutoUpdate: 'never',
    selectServiceFromOutside: false,
    scenarioTemplateId: null,
    SpeedModel: 'FAST_SMART',
  },
  defaultScenario: null,
  expiredDate: null,
  maxUsageKitty: null,
  usageKitty: null,
  maxToken: 1000,
  configuration: null,
  botVersions: [],
  currentPackageName: null,
};

const KnowledgeSlice: any = createSlice({
  name: 'KnowledgeSlice',
  initialState,
  reducers: {
    setChatbotTitle: (state, action: PayloadAction<string>) => {
      state.create.chatbotPrevTitle = state.create.chatbotTitle;
      state.create.chatbotTitle = action.payload;
    },
    setChatBotService: (
      state,
      action: PayloadAction<
        | BotTypeEnum.AI_READY_TO_USE
        | BotTypeEnum.AI_WITH_KNOWLEDGE_BASE
        | BotTypeEnum.SITE_SCAN_PLUG_AND_PLAY
        | BotTypeEnum.TEXT_SCENARIO
        | BotTypeEnum.HUMAN_CLONE
        | BotTypeEnum.VIDEO_SCENARIO
      >
    ) => {
      state.create.chatbotService = action.payload;
      state.create.isSideMenu = true;
      if (action.payload == BotTypeEnum.AI_READY_TO_USE) {
        state.create.step = [...state.create.assistantBotSteps];
      } else if (action.payload == BotTypeEnum.AI_WITH_KNOWLEDGE_BASE) {
        state.create.step = [...state.create.kbAssistantBotSteps];
      } else if (action.payload == BotTypeEnum.SITE_SCAN_PLUG_AND_PLAY) {
        state.create.step = [...state.create.ppAssistantBotSteps];
      } else if (action.payload == BotTypeEnum.TEXT_SCENARIO) {
        if (state.create.isEdit) {
          state.create.step = state.create.scenarioSteps.filter(s => s != 'mainStarterKit');
        } else {
          state.create.step = [...state.create.scenarioSteps];
        }
      } else if (action.payload == BotTypeEnum.VIDEO_SCENARIO) {
        state.create.step = [...state.create.VideoScenarioSteps];
      } else if (action.payload == BotTypeEnum.HUMAN_CLONE) {
        state.create.step = [...state.create.assistantCloneBotSteps];
      }
    },
    setChatBotModel: (state, action: PayloadAction<string>) => {
      state.create.chatbotModel = action.payload;
    },
    setkittyMaxToken: (state, action: PayloadAction<number>) => {
      state.maxToken = action.payload;
    },
    setConfiguration: (state, action: PayloadAction<string>) => {
      state.configuration = action.payload;
    },
    setXlsValue: (state: IKnowledgeState, action: PayloadAction<IXlSFile[]>) => {
      state.create.fileXlsValue = action.payload;
    },
    setFile: (state: IKnowledgeState, action: PayloadAction<File>) => {
      state.create.fileXls = action.payload;
    },
    setFileName: (state, action: PayloadAction<string>) => {
      state.create.fileXlsName = action.payload;
    },
    setAssistant: (state, action: PayloadAction<IAssistant>) => {
      state.create.assistant = action.payload;
    },
    setDomainAddress: (state, action: PayloadAction<string>) => {
      state.create.domainAddress = action.payload;
    },
    setInputColumns: (state, action: PayloadAction<string[]>) => {
      state.create.inputs = action.payload;
    },
    setOutputColumns: (state, action: PayloadAction<string[]>) => {
      state.create.outputs = action.payload;
    },
    setNewQuestion: (state, action: PayloadAction<string>) => {
      const questionName: string = 'Question #' + (state.create.questions.length + 1);
      switch (action.payload) {
        case 'multichoice':
          state.create.questions.push({
            isValid: false,
            type: 'multichoice',
            name: questionName,
            text: '',
            next: '',
            responses: [
              {
                keywords: [],
                next: '',
                text: '',
              },
            ],
          });
          break;
        case 'input':
          state.create.questions.push({
            isValid: false,
            type: 'input',
            name: questionName,
            text: '',
            next: '',
            responses: [
              {
                keywords: [],
                next: '',
                text: '',
              },
            ],
          });
          break;
        default:
          break;
      }
    },
    setQuestion: (state, action: PayloadAction<ICreateKnowledgeQuestion>) => {
      const question = state.create.questions.find(dr => dr.name == action.payload.name);
      if (question) {
        question.type = action.payload.type;
        question.isValid = action.payload.isValid;
        question.text = action.payload.text;
        for (let i = 0; i < action.payload.responses.length; i++) {
          question.responses[i].next = action.payload.responses[i].next;
          question.responses[i].text = action.payload.responses[i].text;
          question.responses[i].keywords = action.payload.responses[i].keywords;
        }
        question.next = action.payload.next;
      }
    },
    removeQuestion: (state, action: PayloadAction<string>) => {
      state.create.questions = state.create.questions.filter(dr => dr.name != action.payload);
    },
    setNewGroupOfKeywords: (state, action: PayloadAction<ICreateKnowledgeQuestion>) => {
      state.create.questions
        .find(dr => dr.name == action.payload.name)
        ?.responses?.push({
          keywords: [],
          next: '',
          text: '',
        });
    },
    setAssistantInformation: (
      state,
      action: PayloadAction<{
        assistantWelcomeMessage?: string;
        assistantCompanyName: string;
        assistantAgentName: string;
        additional_instruction: string;
      }>
    ) => {
      state.create.assistantWelcomeMessage = action.payload.assistantWelcomeMessage;
      state.create.assistantAgentName = action.payload.assistantAgentName;
      state.create.assistantCompanyName = action.payload.assistantCompanyName;
      state.create.additional_instruction = action.payload.additional_instruction;
    },
    setBotVersions: (state, action: PayloadAction<IBotVersions[]>) => {
      state.botVersions = action.payload;
    },
    setIsSideMenuCreateKnowledgeBase: (state, action: PayloadAction<boolean>) => {
      state.create.isSideMenu = action.payload;
    },
    nextStep: (state: IKnowledgeState) => {
      let indexStep = state.create.step.indexOf(state.create.currentStep);

      if (indexStep >= 0 && state.create.step.length - 1 > indexStep) {
        indexStep = indexStep + 1;
        state.create.currentStep = state.create.step[indexStep];

        state.create.stepNumber = indexStep + 1;
      }

      if (indexStep < state.create.step.length - 1) {
        state.create.isNextStep = true;
      } else {
        state.create.isNextStep = false;
      }

      state.create.isPreviousStep = true;
    },
    previousStep: (state: IKnowledgeState) => {
      let indexStep = state.create.step.indexOf(state.create.currentStep);

      if (indexStep > 0) {
        indexStep = indexStep - 1;
        state.create.currentStep = state.create.step[indexStep];

        state.create.stepNumber = indexStep + 1;
      }

      if (indexStep == 0) {
        state.create.isPreviousStep = false;
      } else {
        state.create.isPreviousStep = true;
      }

      state.create.isNextStep = true;
    },
    resetStep: (state: IKnowledgeState) => {
      state.create.stepNumber = 1;
      state.create.currentStep = 'main';
      state.create.isNextStep = true;
      state.create.isPreviousStep = false;
    },
    resetCreate: (state: IKnowledgeState) => {
      const botType = state.create.botType;
      state.create = {
        ...initialState.create,
        botType: botType,
      };

      localStorage.removeItem('persist:knowledge');
    },
    changeBotType: (state: IKnowledgeState, action: PayloadAction<'Widget' | 'Link'>) => {
      state.create.botType = action.payload;
    },
    setPackage: (
      state: IKnowledgeState,
      action: PayloadAction<{
        maxUsageKitty: string | null;
        usageKitty: number | null;
        expiredDate: Date | null;
        defaultScenario: string | null;
      }>
    ) => {
      state.defaultScenario = action.payload.defaultScenario;
      state.expiredDate = action.payload.expiredDate;
      state.maxUsageKitty = action.payload.maxUsageKitty;
      state.usageKitty = action.payload.usageKitty;
    },

    setEditAssistant: (state: IKnowledgeState, action: PayloadAction<IUpdateKnowledgeAssistant>) => {
      state.create.chatbotService =
        state.create.botType == 'Link' ? BotTypeEnum.HUMAN_CLONE : (action.payload.botService as ICreateKnowledge['chatbotService']);
      state.create.id = action.payload.id;
      state.create.isEdit = true;
      state.create.assistantAgentName = action.payload.assistantAgentName;
      state.create.assistantWelcomeMessage = action.payload.assistantWelcomeMessage;
      state.create.assistantCompanyName = action.payload.assistantCompanyName;
      state.create.additional_instruction = action.payload.additional_instruction;
      state.create.chatbotTitle = action.payload.chatbotTitle;
      state.create.chatbotPrevTitle = action.payload.chatbotTitle;
      state.create.assistantInstructions = action.payload.instructions;
      state.create.domainAddress = action.payload.domainAddress ?? '';
      state.create.ppAutoUpdate = action.payload.ppAutoUpdate ?? 'never';
      state.create.chatbotModel = 'gpt-3.5-turbo';
      state.create.SpeedModel = action.payload.SpeedModel;
      state.create.assistant = {
        ...state.create.assistant,
        code: action.payload.assistantCode,
      } as IAssistant;
      state.create.files = action.payload.files || [];

      if (state.create.botType == 'Link') {
        state.create.step = [...state.create.assistantCloneBotSteps];
      } else {
        if (action.payload.botService === BotTypeEnum.AI_READY_TO_USE) {
          state.create.step = [...state.create.assistantBotSteps];
        } else if (action.payload.botService === BotTypeEnum.AI_WITH_KNOWLEDGE_BASE) {
          state.create.step = [...state.create.kbAssistantBotSteps];
        } else if (action.payload.botService === BotTypeEnum.SITE_SCAN_PLUG_AND_PLAY) {
          state.create.step = [...state.create.ppAssistantBotSteps];
        }
      }
    },
    setEditScenario: (state: IKnowledgeState, action: PayloadAction<IUpdateKnowledgeAssistant>) => {
      state.create.chatbotService = action.payload.botService || BotTypeEnum.TEXT_SCENARIO;
      state.create.id = action.payload.id;
      state.create.isEdit = true;
      state.create.chatbotTitle = action.payload.chatbotTitle;
      state.create.chatbotPrevTitle = action.payload.chatbotTitle;
      state.create.step = action.payload.botService == BotTypeEnum.VIDEO_SCENARIO ? [...state.create.VideoScenarioSteps] : [...state.create.scenarioSteps];
    },
    resetPackage: (state: IKnowledgeState) => {
      state.defaultScenario = null;
      state.expiredDate = null;
      state.maxUsageKitty = null;
      state.usageKitty = null;
    },
    setSelectServiceFromOutside: (state: IKnowledgeState) => {
      state.create.selectServiceFromOutside = true;
    },
    setPPAutoUpdate(state: IKnowledgeState, action: PayloadAction<IUpdateKnowledgeAssistant['ppAutoUpdate']>) {
      state.create.ppAutoUpdate = action.payload || 'never';
    },
    setScenarioTemplateId(state: IKnowledgeState, action: PayloadAction<number>) {
      state.create.scenarioTemplateId = action.payload;
    },
    setSpeedModel(state, action: PayloadAction<'SMART' | 'SMART_FAST' | 'FAST_SMART' | 'FAST'>) {
      state.create.SpeedModel = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addMatcher(ChatbotRTKService.endpoints.getPackage.matchFulfilled, (state, action) => {
      if (action?.payload?.plan) {
        state.currentPackageName = action?.payload?.plan;
      }
      if (action?.payload?.config) {
        state.maxToken = action?.payload?.config.aikitty_defaults?.maxToken ?? 1000;
        state.configuration = action?.payload?.config.aikitty_defaults?.configuration;
      }
    });
  },
});

export const { setChatbotTitle, setChatBotService, resetCreate, setBotVersions, resetPackage, setEditScenario } = KnowledgeSlice.actions;

export const selectKnowledgeCreate = (state: RootState) => (state?.knowledgestate as IKnowledgeState).create;

export default KnowledgeSlice.reducer;
